/**
 * Uploads a walkthrough video straight to Blob Storage (D-062), in blocks, with the write-only link
 * the API signed. docs/BUILD.md §10 "Videos as built".
 *
 * Two Blob REST calls, nothing else: Put Block for each 8 MB slice (three at a time, each retried),
 * then Put Block List to commit them in order. The bearer token never goes to storage — the link's
 * signature is the authorisation. The same two calls work against the API's local stand-in on a
 * laptop. Progress moves a block at a time; fetch cannot report bytes within one request.
 */

const BLOB_API_VERSION = '2023-11-03';
const PARALLEL = 3;
const ATTEMPTS = 3;

export class UploadCancelled extends Error {
  constructor() {
    super('The upload was cancelled.');
    this.name = 'UploadCancelled';
  }
}

/** Block ids must all be the same length before base64; the index keeps them in order. */
export function blockId(i: number): string {
  return btoa(`block-${String(i).padStart(6, '0')}`);
}

export function blockListXml(ids: string[]): string {
  return `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
}

const withQuery = (url: string, extra: string) => `${url}${url.includes('?') ? '&' : '?'}${extra}`;

async function put(url: string, body: BodyInit, headers: Record<string, string>, signal?: AbortSignal): Promise<void> {
  let last: unknown;
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    if (signal?.aborted) throw new UploadCancelled();
    try {
      const res = await fetch(url, { method: 'PUT', body, headers: { 'x-ms-version': BLOB_API_VERSION, ...headers }, signal });
      if (res.ok) return;
      // 403 means the link expired or is wrong: retrying cannot help.
      if (res.status === 403) throw new Error('Storage refused the upload link (it may have expired). Start the upload again.');
      last = new Error(`Storage answered ${res.status}.`);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) throw new UploadCancelled();
      if (err instanceof Error && err.message.startsWith('Storage refused')) throw err;
      last = err;
    }
    await new Promise((r) => setTimeout(r, 500 * attempt * attempt));
  }
  throw last instanceof Error ? new Error(`The upload failed: ${last.message}`) : new Error('The upload failed.');
}

export async function uploadVideo(
  file: Blob,
  uploadUrl: string,
  contentType: string,
  blockBytes: number,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const count = Math.max(1, Math.ceil(file.size / blockBytes));
  const ids = Array.from({ length: count }, (_, i) => blockId(i));
  let next = 0;
  let done = 0;
  onProgress(0);
  const worker = async () => {
    while (next < count) {
      const i = next++;
      const slice = file.slice(i * blockBytes, Math.min(file.size, (i + 1) * blockBytes));
      await put(withQuery(uploadUrl, `comp=block&blockid=${encodeURIComponent(ids[i])}`), slice, {}, signal);
      done++;
      onProgress(done / (count + 1));
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, count) }, worker));
  await put(withQuery(uploadUrl, 'comp=blocklist'), blockListXml(ids), { 'content-type': 'application/xml', 'x-ms-blob-content-type': contentType }, signal);
  onProgress(1);
}
